import { useContext } from 'react';
import { Button, Container, Box } from '@mui/material';
import {
  Close as CloseIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Minimize as MinimizeIcon,
} from '@mui/icons-material';
import PT from 'prop-types';
import { ThemeContext, UserContext } from 'context';
import { StyledIconButton } from '../buttons';
import config from '../../config';
import chatbotControls from './hooks/chatbot-controls';

const ChatbotPanel = ({
  isMinimized,
  userEmail,
  projectId,
  minimizeFn,
  closeFn,
}) => {
  const localTheme = useContext(ThemeContext);

  return (
    <>
      <Button
        title="MinimiseTitleBar"
        variant="contained"
        color="primary"
        fullWidth
        component="span"
        size="large"
        sx={{
          whiteSpace: 'nowrap',
          textTransform: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          borderRadius: 0,
        }}
        onClick={minimizeFn}
        data-cy="chatbotMinimise"
        data-testid="chatbotTitleBar"
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuestionAnswerIcon sx={{ marginRight: 1 }} />
          DDB Chatbot
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StyledIconButton
            title="Minimise"
            size="small"
            sx={{
              color: theme => theme.palette.common.white,
            }}
            onClick={minimizeFn}
            icon={<MinimizeIcon />}
            data-testid="minimise-chatbot-button"
            aria-label="minimise-chatbot-button"
          />
          <StyledIconButton
            title="Close"
            size="small"
            sx={{
              color: theme => theme.palette.common.white,
            }}
            onClick={closeFn}
            icon={<CloseIcon />}
            aria-label="close-chatbot-button"
            data-testid="close-chatbot-button"
          />
        </Box>
      </Button>

      <Container
        maxWidth="sm"
        tabIndex={-1}
        sx={{
          borderWidth: 0,
          padding: theme => theme.spacing(1),
          flexGrow: 1,
          display: isMinimized ? 'none' : 'flex',
          flexDirection: 'column',
          bottom: '90px',
        }}
        data-testid="chatbot-panel"
        role="dialog"
        aria-label="chatbot-panel"
      >
        <iframe
          style={{
            padding: theme => theme.spacing(0.5),
            width: '100%',
            height: '100%',
            borderWidth: 0,
            flexGrow: 1,
            display: isMinimized ? 'none' : 'block',
          }}
          name="embed-feed"
          title="Chatbot"
          data-testid="chatbot-iframe"
          src={`${config.url.api.base}/chatbot?user=${userEmail}&theme=${localTheme}&projectId=${projectId}`}
        />
      </Container>
    </>
  );
};

ChatbotPanel.propTypes = {
  isMinimized: PT.bool.isRequired,
  userEmail: PT.string.isRequired,
  projectId: PT.string.isRequired,
  minimizeFn: PT.func.isRequired,
  closeFn: PT.func.isRequired,
};

const Chatbot = () => {
  const {
    chatbotIsOpen,
    isMinimized,
    isSmallScreen,
    projectId,
    toggleChatbotIsMinimized,
    toggleChatbotIsOpen,
  } = chatbotControls();

  const { user } = useContext(UserContext);

  const handleHeaderButtonClick = () => {
    return chatbotIsOpen && isMinimized
      ? toggleChatbotIsMinimized()
      : toggleChatbotIsOpen();
  };

  return (
    <>
      {isSmallScreen ? (
        <StyledIconButton
          title="Open DDB chatbot"
          aria-label="chatbot-header-button"
          data-testid="chatbot-header-button"
          onClick={handleHeaderButtonClick}
          icon={<QuestionAnswerIcon />}
        />
      ) : (
        <Button
          title="Open DDB chatbot"
          component="a"
          onClick={handleHeaderButtonClick}
          aria-label="chatbot-header-button"
          data-testid="chatbot-header-button"
          startIcon={<QuestionAnswerIcon />}
        >
          CHATBOT
        </Button>
      )}

      {chatbotIsOpen && (
        <Box
          sx={{
            position: 'fixed',
            right: '10px',
            bottom: '10px',
            top: isMinimized ? 'auto' : '90px',
            height: isMinimized ? 'auto' : 'calc(100% - 120px)',
            width: isMinimized ? 'auto' : '600px',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            // eslint-disable-next-line no-confusing-arrow
            backgroundColor: theme =>
              theme.palette.mode === 'dark'
                ? theme.palette.common.black
                : theme.palette.common.white,
            // eslint-disable-next-line no-confusing-arrow
            boxShadow: theme =>
              theme.palette.mode === 'dark'
                ? '0px 0px 10px rgba(255, 255, 255, 0.25)' // Lighter shadow for dark mode
                : '0px 0px 10px rgba(0, 0, 0, 0.25)',
            transform: 'translateX(0)',
          }}
        >
          {isMinimized ? (
            <StyledIconButton
              title="Expand"
              color="primary"
              onClick={toggleChatbotIsMinimized}
              data-TestId={'maximise-chatbot-button'}
              sx={{
                position: 'fixed',
                bottom: '50px',
                right: '50px',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                backgroundColor: theme => theme.palette.primary.main,
                color: theme => theme.palette.common.white,
                '&:hover': {
                  backgroundColor: theme => theme.palette.primary.dark,
                },
              }}
              icon={<QuestionAnswerIcon sx={{ fontSize: '33px' }} />}
            />
          ) : (
            <ChatbotPanel
              isMinimized={isMinimized}
              userEmail={user.email}
              projectId={projectId}
              minimizeFn={toggleChatbotIsMinimized}
              closeFn={toggleChatbotIsOpen}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default Chatbot;
